/* §Run-Script Pass — Ad-hoc admin `Run Custom Script` tool.

   Lets an authenticated Studio G4 admin submit Python or shell script text
   and stream stdout (+ merged stderr) back line-by-line over Server-
   Sent Events.  Intentional remote code execution — audited server-
   side; no confirmation gate is imposed here as the spec explicitly
   trusts all admins.

   Styling adheres to the Studio G4 palette: charcoal (#1D1D1B), cream
   (#F5F3EE), terracotta (#A8543F).  Manrope for headings; monospace
   (system default) inside editor + output panes.  No purple gradients. */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Terminal, X, Play, Loader2, Trash2, BookOpen } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* -------------------------------------------------------------------- */
/* Sample scripts library — stdlib-only Python + ops-relevant shell.    */
/* -------------------------------------------------------------------- */

const SAMPLE_SCRIPTS = [
    { group: "Shell Scripts", type: "shell", slug: "system-information",
      title: "System Information",
      description: "Display system details, disk usage, and memory status",
      tags: "uname, df, free",
      content: 'uname -a\necho "---"\ndf -h\necho "---"\nfree -h' },
    { group: "Shell Scripts", type: "shell", slug: "list-db-collections",
      title: "List Database Collections",
      description: "Show all collections and their document counts",
      tags: "mongosh",
      content:
        'mongosh "$MONGO_URL" --quiet --eval \'const d = db.getSiblingDB(process.env.DB_NAME); d.getCollectionNames().forEach(function(c){ print(c + ": " + d.getCollection(c).countDocuments()); });\'' },
    { group: "Shell Scripts", type: "shell", slug: "find-enquiry-by-email",
      title: "Find Enquiry by Email",
      description: "Look up a specific enquiry by email address — edit EMAIL below before running",
      tags: "mongosh findOne",
      content:
        'EMAIL="admin@example.com"\nmongosh "$MONGO_URL" --quiet --eval \'const d = db.getSiblingDB(process.env.DB_NAME); printjson(d.enquiries.findOne({ email: process.env.EMAIL }));\'' },
    { group: "Shell Scripts", type: "shell", slug: "recent-enquiries",
      title: "Recent Enquiries",
      description: "Display the 10 most recent enquiries",
      tags: "mongosh sort/limit",
      content:
        'mongosh "$MONGO_URL" --quiet --eval \'const d = db.getSiblingDB(process.env.DB_NAME); d.enquiries.find({}, { full_name: 1, email: 1, postcode: 1, created_at: 1 }).sort({ created_at: -1 }).limit(10).forEach(function(x){ printjson(x); });\'' },
    { group: "Shell Scripts", type: "shell", slug: "database-statistics",
      title: "Database Statistics",
      description: "Show database size and top collection stats",
      tags: "mongosh dbStats",
      content:
        'mongosh "$MONGO_URL" --quiet --eval \'const d = db.getSiblingDB(process.env.DB_NAME); printjson(d.stats());\'' },
    { group: "Python Scripts", type: "python", slug: "python-environment",
      title: "Python Environment",
      description: "Display Python version and environment details",
      tags: "os, sys, datetime",
      content:
        'import os, sys\nfrom datetime import datetime, timezone\nprint("Python:", sys.version)\nprint("Executable:", sys.executable)\nprint("CWD:", os.getcwd())\nprint("Now (UTC):", datetime.now(timezone.utc).isoformat())' },
    { group: "Python Scripts", type: "python", slug: "list-directory-contents",
      title: "List Directory Contents",
      description: "Show files and folders in a directory — edit TARGET below before running",
      tags: "os.listdir",
      content:
        'import os\nTARGET = "/app/backend"\nfor name in sorted(os.listdir(TARGET)):\n    full = os.path.join(TARGET, name)\n    kind = "DIR " if os.path.isdir(full) else "FILE"\n    print(kind, name)' },
    { group: "Python Scripts", type: "python", slug: "check-environment-keys",
      title: "Check Environment Keys",
      description: "List all environment variable names (values redacted)",
      tags: "os.environ",
      content:
        'import os\nfor k in sorted(os.environ.keys()):\n    print(k)' },
    { group: "Python Scripts", type: "python", slug: "update-environment-key",
      title: "Update Environment Key",
      description: "⚠ Modifies /app/backend/.env — restart backend to take effect.",
      tags: "file I/O, .env update",
      content:
        '# ⚠ Writes to /app/backend/.env. Restart backend afterwards.\nfrom pathlib import Path\nKEY = "MY_KEY"        # edit\nVALUE = "my-value"    # edit\nENV_PATH = Path("/app/backend/.env")\nlines = ENV_PATH.read_text().splitlines() if ENV_PATH.exists() else []\nout, replaced = [], False\nfor line in lines:\n    if line.startswith(KEY + "="):\n        out.append(f\"{KEY}=\\\"{VALUE}\\\"\")\n        replaced = True\n    else:\n        out.append(line)\nif not replaced:\n    out.append(f\"{KEY}=\\\"{VALUE}\\\"\")\nENV_PATH.write_text(\"\\n\".join(out) + \"\\n\")\nprint(f\"{KEY} {\'updated\' if replaced else \'added\'}. Restart backend to take effect.\")' },
    { group: "Python Scripts", type: "python", slug: "date-calculations",
      title: "Date Calculations",
      description: "Useful date/time calculations and formatting",
      tags: "datetime, timedelta",
      content:
        'from datetime import datetime, timedelta, timezone\nnow = datetime.now(timezone.utc)\nprint("Now:", now.isoformat())\nprint("Yesterday:", (now - timedelta(days=1)).isoformat())\nprint("+30 days:", (now + timedelta(days=30)).isoformat())\nprint("Start of month:", now.replace(day=1, hour=0, minute=0, second=0, microsecond=0).isoformat())' },
];

/* -------------------------------------------------------------------- */
/* SampleScriptsModal                                                    */
/* -------------------------------------------------------------------- */

export function SampleScriptsModal({ onPick, onClose }) {
    const groups = useMemo(() => {
        const g = {};
        for (const s of SAMPLE_SCRIPTS) (g[s.group] ||= []).push(s);
        return g;
    }, []);
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
             data-testid="sample-scripts-modal">
            <div className="bg-white rounded-lg shadow-lg w-[640px] max-w-[92vw] max-h-[85vh] flex flex-col border border-[#E5E2DC]">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E2DC] bg-[#1D1D1B] text-[#F5F3EE] rounded-t-lg">
                    <div>
                        <div className="font-manrope font-semibold text-[15px]">Sample Scripts</div>
                        <div className="text-[12px] opacity-80">Click on any script to copy it to the editor</div>
                    </div>
                    <button onClick={onClose}
                        aria-label="Close"
                        data-testid="sample-scripts-close"
                        className="text-[#F5F3EE] hover:text-white p-1 rounded-md">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-4 overflow-y-auto flex-1 space-y-4">
                    {Object.entries(groups).map(([group, items]) => (
                        <div key={group} className="flex flex-col gap-2">
                            <div className="text-[11px] uppercase tracking-wide font-manrope font-semibold text-[#666]">{group}</div>
                            <div className="flex flex-col gap-1.5">
                                {items.map(s => (
                                    <button
                                        key={s.slug}
                                        onClick={() => onPick(s)}
                                        data-testid={`sample-script-${s.slug}`}
                                        className="text-left px-3 py-2 border border-[#E5E2DC] rounded-md hover:border-terracotta hover:bg-[#FBFAF6] transition-colors">
                                        <div className="font-manrope font-semibold text-[13px] text-charcoal">{s.title}</div>
                                        <div className="text-[12px] text-[#666] leading-snug mt-0.5">{s.description}</div>
                                        <div className="text-[11px] font-mono text-terracotta mt-1">{s.tags}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* RunCustomScriptModal                                                  */
/* -------------------------------------------------------------------- */

export default function RunCustomScriptModal({ onClose }) {
    const [lang, setLang] = useState("python");
    const [scriptText, setScriptText] = useState("");
    const [output, setOutput] = useState("");
    const [exitCode, setExitCode] = useState(null);
    const [running, setRunning] = useState(false);
    const [showSamples, setShowSamples] = useState(false);
    const outputRef = useRef(null);
    const abortRef = useRef(null);

    const lineCount = useMemo(
        () => scriptText === "" ? 0 : scriptText.split("\n").length,
        [scriptText]);

    useEffect(() => {
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    }, [output]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && !running) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, running]);

    const clearAll = () => { setOutput(""); setExitCode(null); };

    const runScript = async () => {
        if (!scriptText.trim() || running) return;
        setOutput("");
        setExitCode(null);
        setRunning(true);
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const resp = await fetch(`${API_BASE}/api/admin/tools/run-script`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    script_type: lang,
                    script_content: scriptText,
                }),
                signal: controller.signal,
            });
            if (!resp.ok) {
                let msg = `HTTP ${resp.status}`;
                try {
                    const j = await resp.json();
                    if (j?.detail) msg += `: ${typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail)}`;
                } catch { /* ignore */ }
                setOutput(msg + "\n");
                setExitCode(resp.status);
                return;
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n");
                buffer = parts.pop() ?? "";  // keep incomplete tail
                for (const raw of parts) {
                    if (!raw.startsWith("data: ")) continue;
                    let evt;
                    try { evt = JSON.parse(raw.slice(6)); } catch { continue; }
                    if (evt.type === "output") {
                        setOutput(prev => prev + evt.content);
                    } else if (evt.type === "error") {
                        setOutput(prev => prev + `[error] ${evt.message}\n`);
                    } else if (evt.type === "complete") {
                        setExitCode(evt.exit_code);
                    }
                }
            }
        } catch (err) {
            if (err.name !== "AbortError") {
                setOutput(prev => prev + `[client error] ${err.message}\n`);
                setExitCode(-1);
            }
        } finally {
            setRunning(false);
            abortRef.current = null;
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
             data-testid="run-script-modal">
            <div className="bg-white rounded-lg shadow-xl w-[900px] max-w-[92vw] h-[80vh] max-h-[92vh] flex flex-col border border-[#E5E2DC] overflow-hidden">
                {/* Header ------------------------------------------------ */}
                <div className="flex items-center justify-between px-5 py-3 bg-[#1D1D1B] text-[#F5F3EE]">
                    <div className="flex items-center gap-2">
                        <Terminal className="w-5 h-5 text-terracotta" />
                        <div>
                            <div className="font-manrope font-semibold text-[15px]">Run Custom Script</div>
                            <div className="text-[12px] opacity-80">Execute scripts on the server</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 bg-[#2A2A28] rounded-md p-0.5">
                            {["python", "shell"].map(l => {
                                const active = lang === l;
                                return (
                                    <button
                                        key={l}
                                        onClick={() => setLang(l)}
                                        disabled={running}
                                        data-testid={`run-script-lang-${l}`}
                                        className={`text-[12px] px-3 py-1 rounded ${
                                            active
                                                ? "bg-terracotta text-white"
                                                : "text-[#CFCCC5] hover:text-[#F5F3EE]"
                                        } ${running ? "opacity-60 cursor-not-allowed" : ""}`}>
                                        {l === "python" ? "Python" : "Shell"}
                                    </button>
                                );
                            })}
                        </div>
                        <button onClick={onClose}
                            disabled={running}
                            aria-label="Close"
                            data-testid="run-script-close"
                            className="text-[#F5F3EE] hover:text-white p-1 rounded-md disabled:opacity-40">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Body: split-pane ------------------------------------- */}
                <div className="flex-1 grid grid-cols-2 min-h-0">
                    {/* Editor */}
                    <div className="flex flex-col border-r border-[#E5E2DC] min-h-0">
                        <div className="flex items-center justify-between px-4 py-2 bg-[#F5F3EE] border-b border-[#E5E2DC]">
                            <div className="font-manrope font-semibold text-[12px] text-charcoal">
                                {lang === "python" ? "Python Script" : "Shell Script"}
                            </div>
                            <div className="text-[11px] text-[#666] font-mono">
                                {lineCount} {lineCount === 1 ? "line" : "lines"}
                            </div>
                        </div>
                        <textarea
                            value={scriptText}
                            onChange={e => setScriptText(e.target.value)}
                            spellCheck={false}
                            data-testid="run-script-editor"
                            placeholder={lang === "python"
                                ? "# Enter your Python script here..."
                                : "# Enter your shell script here..."}
                            className="flex-1 w-full resize-none bg-[#111111] text-slate-100 font-mono text-[13px] leading-relaxed p-4 outline-none whitespace-pre overflow-auto" />
                    </div>

                    {/* Output */}
                    <div className="flex flex-col min-h-0">
                        <div className="flex items-center justify-between px-4 py-2 bg-[#F5F3EE] border-b border-[#E5E2DC]">
                            <div className="font-manrope font-semibold text-[12px] text-charcoal">Output</div>
                            {exitCode !== null && (
                                <span
                                    data-testid="run-script-exit-badge"
                                    className={`text-[11px] px-2 py-0.5 rounded-full font-mono ${
                                        exitCode === 0
                                            ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                            : "bg-terracotta text-white border border-terracotta"
                                    }`}>
                                    Exit {exitCode}
                                </span>
                            )}
                        </div>
                        <pre
                            ref={outputRef}
                            data-testid="run-script-output"
                            className="flex-1 w-full bg-[#111111] text-slate-100 font-mono text-[13px] leading-relaxed p-4 overflow-auto whitespace-pre-wrap">
                            {output === "" && !running
                                ? <span className="text-slate-500">Output will appear here...</span>
                                : output}
                            {running && output === "" && (
                                <span className="text-slate-400">Running…</span>
                            )}
                        </pre>
                    </div>
                </div>

                {/* Footer ------------------------------------------------ */}
                <div className="flex items-start justify-between gap-4 px-4 py-3 bg-[#F5F3EE] border-t border-[#E5E2DC]">
                    <div className="text-[11px] text-[#333] leading-snug max-w-[55%]">
                        <span className="font-semibold">Note:</span> Scripts run with elevated privileges. Create a backup first for risky operations. <span className="font-semibold">Tip:</span> Use Shell with <code className="font-mono">mongosh</code> for database queries. Use Python for basic system tasks.
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowSamples(true)}
                            disabled={running}
                            data-testid="run-script-samples-open"
                            className="text-[12px] px-3 py-1.5 rounded-md bg-white text-terracotta border border-[#E5E2DC] hover:bg-[#FBFAF6] inline-flex items-center gap-1.5 disabled:opacity-40">
                            <BookOpen className="w-4 h-4" /> Sample Scripts
                        </button>
                        <button
                            onClick={clearAll}
                            disabled={running}
                            data-testid="run-script-clear"
                            className="text-[12px] px-3 py-1.5 rounded-md bg-white text-charcoal border border-[#E5E2DC] hover:bg-[#FBFAF6] inline-flex items-center gap-1.5 disabled:opacity-40">
                            <Trash2 className="w-4 h-4" /> Clear
                        </button>
                        <button
                            onClick={runScript}
                            disabled={running || !scriptText.trim()}
                            data-testid="run-script-run"
                            className="text-[12px] px-3 py-1.5 rounded-md bg-charcoal text-[#F5F3EE] hover:bg-[#2A2A28] inline-flex items-center gap-1.5 disabled:opacity-60">
                            {running
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Play className="w-4 h-4" />}
                            {running ? "Running…" : "Run Script"}
                        </button>
                    </div>
                </div>
            </div>

            {showSamples && (
                <SampleScriptsModal
                    onPick={(s) => {
                        setScriptText(s.content);
                        setLang(s.type);
                        setShowSamples(false);
                    }}
                    onClose={() => setShowSamples(false)} />
            )}
        </div>
    );
}
